'use client'

import { useEffect, useState } from 'react'

import { BookConditionsEnum, DeliveryMethodsEnum } from '@hermes/types'

import SortAndFilterBar from '@components/SortAndFilterBar'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { ReadonlyURLSearchParams } from 'next/navigation'
import { ConditionFilterField, FilterData, ModalTypes, SortData } from './types'

interface Props {
  onChange: () => void
  params: ReadonlyURLSearchParams
}

const useSortAndFilterBar = ({ onChange, params }: Props) => {
  const deliveryMethod = (params?.get('deliveryMethod') as 'shipping' | 'meetup') || 'shipping'
  const { t } = useTranslateMessage()

  const [sortModalOpen, setSortModalOpen] = useState(false)
  const [filterModalOpen, setFilterModalOpen] = useState(false)
  const [sortData, setSortData] = useState<SortData>([
    { label: t({ id: 'sortTypes.score' }), type: 'SCORE', value: 'score', active: true },
    { label: t({ id: 'sortTypes.cheapest' }), type: 'PRICE', value: 'price', active: false }
  ])
  const [filterData, setFilterData] = useState<FilterData>({
    DELIVERY_METHOD: {
      title: t({ id: 'common.delivery_method', defaultMessage: 'Delivery method' }),
      options: [
        {
          label: t({ id: 'common.delivery_method_home_to_home', defaultMessage: 'Home to home delivery' }),
          category: 'DELIVERY_METHOD',
          type: 'DELIVERY_METHOD_SHIPPING',
          value: DeliveryMethodsEnum.SHIPPING,
          active: deliveryMethod ? deliveryMethod === DeliveryMethodsEnum.SHIPPING : true
        },
        {
          label: t({ id: 'common.delivery_method_meetup', defaultMessage: 'Meetup' }),
          category: 'DELIVERY_METHOD',
          type: 'DELIVERY_METHOD_MEETUP',
          value: DeliveryMethodsEnum.MEETUP,
          active: deliveryMethod ? deliveryMethod === DeliveryMethodsEnum.MEETUP : false
        }
      ]
    },
    CONDITION: {
      title: t({ id: 'common.condtion', defaultMessage: 'Condition' }),
      options: [
        {
          label: t({ id: 'common.like_new', defaultMessage: 'Like new' }),
          category: 'CONDITION',
          type: 'CONDITION_LIKE_NEW',
          value: BookConditionsEnum.LIKE_NEW,
          active: false
        },
        {
          label: t({ id: 'common.very_good', defaultMessage: 'Very good' }),
          category: 'CONDITION',
          type: 'CONDITION_VERY_GOOD',
          value: BookConditionsEnum.VERY_GOOD,
          active: false
        },
        {
          label: t({ id: 'common.good', defaultMessage: 'Good' }),
          category: 'CONDITION',
          type: 'CONDITION_GOOD',
          value: BookConditionsEnum.GOOD,
          active: false
        },
        {
          label: t({ id: 'common.acceptable', defaultMessage: 'Acceptable' }),
          category: 'CONDITION',
          type: 'CONDITION_ACCEPTABLE',
          value: BookConditionsEnum.ACCEPTABLE,
          active: false
        },
        {
          label: t({ id: 'common.refurbished', defaultMessage: 'Refurbished' }),
          category: 'CONDITION',
          type: 'CONDITION_REFURBISHED',
          value: BookConditionsEnum.REFURBISHED,
          active: false
        }
      ]
    }
  })

  const toggleModalOpen = (type: ModalTypes) => {
    if (type === ModalTypes.SORT) {
      !filterModalOpen && setSortModalOpen(!sortModalOpen)
    }
    if (type === ModalTypes.FILTER) {
      !sortModalOpen && setFilterModalOpen(!filterModalOpen)
    }
  }

  const handleSortSubmit = (data: SortData) => {
    setSortData(data)
    toggleModalOpen(ModalTypes.SORT)
    onChange()
  }

  const handleFilterSubmit = (data: FilterData) => {
    setFilterData(data)
    toggleModalOpen(ModalTypes.FILTER)
    onChange()
  }

  const onConditionChange = (type: ConditionFilterField['type']) => {
    const newOptions = [] as ConditionFilterField[]
    const filterDataCopy = { ...filterData }

    filterDataCopy.CONDITION.options.forEach((o) => {
      const newObject = o
      if (o.type === type) {
        newObject.active = false
      }
      newOptions.push(o)
    })
    filterDataCopy.CONDITION.options = newOptions

    setFilterData(filterDataCopy)
    onChange()
  }

  useEffect(() => {
    if (deliveryMethod) {
      const newOptions = [] as FilterData['DELIVERY_METHOD']['options']
      const filterDataCopy = { ...filterData }
      filterDataCopy.DELIVERY_METHOD.options.forEach((o) => {
        const newObject = o
        newObject.active = deliveryMethod === o.value
        newOptions.push(o)
      })
      filterDataCopy.DELIVERY_METHOD.options = newOptions

      setFilterData(filterDataCopy)
    }
  }, [deliveryMethod])

  return {
    SortAndFilterBar: () => (
      <SortAndFilterBar
        filterData={filterData}
        sortData={sortData}
        filterModalOpen={filterModalOpen}
        sortModalOpen={sortModalOpen}
        chipClicked={onConditionChange}
        handleFilterSubmit={handleFilterSubmit}
        handleSortSubmit={handleSortSubmit}
        toggleModalOpen={toggleModalOpen}
      />
    ),
    filterData,
    sortData
  }
}

export default useSortAndFilterBar
